import { Skeleton } from "@/components/atoms";
import { useUIColors } from "@/core/hooks";
import React from "react";
import { Platform, StyleSheet, View } from "react-native";

export interface StatusRowLayoutLike {
  stackStatusRow: boolean;
  matchStatusRowHeights: boolean;
  statusRowMinHeight: number | undefined;
  spreadStatusActions?: boolean;
}

export interface AppointmentCardSkeletonProps {
  isMobile?: boolean;
  isTablet?: boolean;
  statusRowLayout: StatusRowLayoutLike;
}

/** Placeholder for the upcoming appointment card while GET /appointments/upcoming resolves. */
export function AppointmentCardSkeleton({
  isMobile = false,
  statusRowLayout,
}: Readonly<AppointmentCardSkeletonProps>) {
  const c = useUIColors();
  const { matchStatusRowHeights, statusRowMinHeight, spreadStatusActions, stackStatusRow } =
    statusRowLayout;
  const useSpreadActions = spreadStatusActions ?? isMobile;

  const webBaseColor = Platform.OS === 'web' ? c.skeletonBaseDark : undefined;
  const webHighlightColor = Platform.OS === 'web' ? c.skeletonHighlightDark : undefined;

  return (
    <View
      style={[
        styles.card,
        stackStatusRow && styles.cardStackedFullWidth,
        matchStatusRowHeights && styles.statusCardFillHeight,
        matchStatusRowHeights && styles.cardStatusRow,
        matchStatusRowHeights &&
          statusRowMinHeight != null && { minHeight: statusRowMinHeight },
        isMobile && styles.cardMobile,
        { backgroundColor: Platform.OS === 'web' ? c.backgroundLevel2 : c.backgroundLevel1, borderColor: c.outlineInactive },
      ]}
    >
      <View style={styles.left}>
        <Skeleton width={40} height={40} variant="circular" baseColor={webBaseColor} highlightColor={webHighlightColor} />
        <View style={styles.details}>
          <Skeleton width={170} height={20} borderRadius={4} baseColor={webBaseColor} highlightColor={webHighlightColor} />
          <Skeleton width={260} height={16} borderRadius={4} baseColor={webBaseColor} highlightColor={webHighlightColor} />
          <Skeleton width={220} height={16} borderRadius={4} baseColor={webBaseColor} highlightColor={webHighlightColor} />
        </View>
      </View>

      <View style={[styles.right, useSpreadActions && styles.rightMobile]}>
        <Skeleton width={60} height={20} borderRadius={4} baseColor={webBaseColor} highlightColor={webHighlightColor} />
        <Skeleton width={124} height={40} borderRadius={4} baseColor={webBaseColor} highlightColor={webHighlightColor} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    alignContent: "center",
    gap: 12,
    flexWrap: "wrap",
    maxWidth: 660,
    minWidth: 280,
    width: "100%",
  },
  statusCardFillHeight: {
    flex: 1,
    alignSelf: "stretch",
    width: "100%",
    ...(Platform.OS === 'web' ? ({ height: '100%' } as object) : {}),
  },
  cardStatusRow: {
    flex: 1,
    ...(Platform.OS === 'web' ? ({ height: '100%' } as object) : {}),
  },
  cardMobile: {
    maxWidth: "100%",
  },
  cardStackedFullWidth: {
    maxWidth: "100%",
    width: "100%",
    alignSelf: "stretch",
  },
  left: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  details: {
    gap: 5,
    flexShrink: 1,
    minWidth: 0,
  },
  right: {
    flexDirection: "row",
    alignItems: "center",
    gap: 32,
    flexGrow: 0,
    flexShrink: 0,
    marginStart: "auto",
  },
  rightMobile: {
    width: "100%",
    flexGrow: 1,
    flexShrink: 1,
    justifyContent: "space-between",
    gap: 12,
    marginStart: 0,
  },
});
